import { fabric } from 'fabric';
import { AssetVersionCommentDto } from '@api/Api';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import type { HighlightCreatorViewerActions } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import { pinPointCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/pinHandlers';
import { fitPinSize } from '@pages/MediaViewer/AssetViewer/Canvas/utils/fitObject';
import changeObjectPosition from '@pages/MediaViewer/AssetViewer/Canvas/utils/changeObjectsPosition';

type FitPinAssetType = Parameters<typeof fitPinSize>[1];

type RenderPdfPageAnnotationsParams = {
  canvas: fabric.Canvas;
  pageNumber: number;
  commentsList: AssetVersionCommentDto[];
  zoomFactor: number;
  turnoverDegree: number;
  isHideComments: boolean;
  assetType?: FitPinAssetType;
  overObject?: { current: boolean };
  getUserPicture: (commentId: string) => {
    name: string;
    email: string;
    userId: string;
  };
  viewerActions: HighlightCreatorViewerActions;
  onSelectComment: (commentId: string) => void;
};

export function renderPdfPageAnnotationsOnCanvas({
  canvas,
  pageNumber,
  commentsList,
  zoomFactor,
  turnoverDegree,
  isHideComments,
  assetType,
  overObject,
  getUserPicture,
  viewerActions,
  onSelectComment
}: RenderPdfPageAnnotationsParams) {
  canvas.clear();
  canvas.setViewportTransform([zoomFactor * 10, 0, 0, zoomFactor * 10, 0, 0]);

  if (isHideComments) {
    canvas.requestRenderAll();
    return;
  }

  const comments = commentsList.filter(
    (comment) => (comment.documentPage ?? 1) === pageNumber && !comment.isCopied
  );
  const commentsWithAnnotations = comments.filter(
    (comment) => !!comment.annotations
  );
  const commentsWithDocumentHighlight = comments.filter(
    (comment) => !!comment.documentHighlightRects?.length
  );

  const pageViewerActions: HighlightCreatorViewerActions = {
    selectComment: (commentId) => {
      if (!commentId) {
        viewerActions.selectComment(null);
        return;
      }
      onSelectComment(commentId);
    },
    setCanvasMode: viewerActions.setCanvasMode,
    setDocumentHighlight: viewerActions.setDocumentHighlight
  };

  commentsWithAnnotations.forEach((comment) => {
    let parsed: { type?: string; left?: number; top?: number }[];
    try {
      parsed = JSON.parse(comment.annotations as string);
    } catch {
      return;
    }
    const pin = parsed.find((item) => item.type === 'pin');
    const annotation = pin || parsed[0];
    if (
      !annotation ||
      typeof annotation.left !== 'number' ||
      typeof annotation.top !== 'number'
    ) {
      return;
    }

    const { email, name } = getUserPicture(comment.id);
    pinPointCreator({
      canvas,
      scale: fitPinSize(zoomFactor, assetType, canvas),
      commentId: comment.id,
      userEmail: email,
      userName: name,
      documentHighlight: comment.documentHighlight,
      documentHighlightRects: comment.documentHighlightRects,
      x: annotation.left,
      y: annotation.top,
      overObject,
      viewerActions: pageViewerActions,
      callback: () =>
        changeObjectPosition({
          canvas,
          angle: turnoverDegree
        })
    });
  });

  commentsWithDocumentHighlight.forEach((comment) => {
    highlightCreator({
      canvas,
      overObject,
      viewerActions: pageViewerActions,
      commentId: comment.id,
      documentHighlight: comment.documentHighlight,
      documentHighlightRects: comment.documentHighlightRects
    });
  });

  canvas.requestRenderAll();
}
